import { useClientUser } from '@/hooks';
import React, { useEffect, useMemo, useState } from 'react';
import { TabCard } from '../blocks';
import { css } from '@emotion/react';
import { Autocomplete } from '@material-ui/lab';
import { Checkbox, FormControlLabel, Grid, IconButton, Switch, TextField } from '@material-ui/core';
import { useMutation } from '@apollo/client';
import { DELETE_TAG, UPSERT_TAGS, REPLACE_ALWAYS_APPLY_CLIENTS } from './graphql';
import { captureException } from '@sentry/react';
import { CLIENT_BY_ID } from '@/graphql/queries/users';
import { TAG_TYPES } from './constants';
import { DeleteForeverOutlined } from '@material-ui/icons';
import Snackbar from '@/components/Snackbar';
import NavResponsiveModal, {
    ModalActions,
    ModalContent,
    ModalHeader,
} from '@/components/Navigation/NavResponsiveModal';
import { Body1 } from '../blocks';
import { PrimaryButton, SecondaryButton } from '@/styles/blocks';

// "Always apply" for an account-owned tag is the accounts own client_id
const AlwaysApplyToggle = ({ checked, onToggle }) => {
    const [on, setOn] = useState(!!checked);
    useEffect(() => setOn(!!checked), [checked]);

    return (
        <FormControlLabel
            labelPlacement="top"
            label="Always Apply"
            control={
                <Switch
                    color="primary"
                    checked={on}
                    onChange={(e) => {
                        setOn(e.target.checked);
                        onToggle(e.target.checked);
                    }}
                />
            }
        />
    );
};

const CustomTags = () => {
    const { tags, user_id } = useClientUser();
    const [notification, setNotification] = useState({});
    const [updates, setUpdates] = useState({});
    const [deleteModal, setDeleteModal] = useState(false);

    // `tags` now includes org-level tags this client inherits. Only client-owned tags
    // are editable here, org tags render in a separate read-only section below.
    const ownedTags = useMemo(() => tags.filter((tag) => tag.client_id === user_id), [tags, user_id]);
    const orgTags = useMemo(() => tags.filter((tag) => !tag.client_id && tag.organization_id), [tags]);

    const sortedTags = useMemo(() => {
        return [...ownedTags].sort((a, b) => a.tag.localeCompare(b.tag));
    }, [ownedTags]);

    const updated = useMemo(() => {
        // Create a map of all saved tags by ID
        const savedByKey = Object.fromEntries(sortedTags.map((t) => [t.tag_id, t]));

        // Combine with updates
        const allTags = {
            ...savedByKey,
            ...updates,
        };

        return sortedTags.map((tag) => allTags[tag.tag_id] || tag);
    }, [sortedTags, updates]);

    const [upsertTags, { loading: upsertLoading }] = useMutation(UPSERT_TAGS, {
        refetchQueries: [CLIENT_BY_ID],
        awaitRefetchQueries: true,
        onError: (e) => {
            console.error(e);
            captureException(e);
            setNotification({
                severity: 'error',
                message: 'Error updating tags',
            });
        },
        onCompleted: () => {
            // Clear updates
            setUpdates({});

            // Show success notification
            setNotification({
                severity: 'success',
                message: 'Tags saved successfully and sorted.',
            });
        },
    });

    const [replaceAlwaysApply] = useMutation(REPLACE_ALWAYS_APPLY_CLIENTS, {
        refetchQueries: [CLIENT_BY_ID],
        awaitRefetchQueries: true,
        onError: (e) => {
            console.error(e);
            captureException(e);
            setNotification({ severity: 'error', message: 'Error saving always-apply' });
        },
    });

    const handleSave = async () => {
        const dirtyIds = Object.keys(updates);
        try {
            const res = await upsertTags({
                variables: {
                    tags: updated.map(({ __typename, client, always_apply_clients, ...rest }) => rest),
                },
            });
            if (res?.data && !res?.errors?.length && dirtyIds.length) {
                // Replace existing always apply tags for the client
                const rows = dirtyIds
                    .map((id) => updated.find((t) => t.tag_id === id))
                    .filter(Boolean)
                    .flatMap((t) =>
                        (t.always_apply_clients || []).map((c) => ({ tag_id: t.tag_id, client_id: c.client_id }))
                    );
                await replaceAlwaysApply({ variables: { tag_ids: dirtyIds, rows } });
            }
        } catch (e) {
            // the mutations' onError handlers already surfaced the message
        }
    };

    const [deleteTag, { loading: deleteLoading }] = useMutation(DELETE_TAG, {
        onError: (e) => {
            console.error(e);
            captureException(e);
            setNotification({
                severity: 'error',
                message: 'Error deleting tag. Please try again.',
            });
        },
        update: (cache, { data: { delete_tags_by_pk } }) => {
            cache.evict(cache.identify(delete_tags_by_pk));
        },
    });

    const closeDeleteModal = () => {
        setDeleteModal(false);
    };

    const handleModalDelete = () => {
        deleteTag({ variables: { tag_id: deleteModal.tag_id } });
        closeDeleteModal();
    };

    const loading = upsertLoading || deleteLoading;

    return (
        <>
            <NavResponsiveModal open={!!deleteModal} onClose={closeDeleteModal}>
                <ModalHeader title="Delete Tag?" onClose={closeDeleteModal} />
                <ModalContent>
                    <Body1>Are you sure you want to delete this tag?</Body1>
                </ModalContent>
                <ModalActions>
                    <SecondaryButton onClick={closeDeleteModal}>Cancel</SecondaryButton>
                    <PrimaryButton onClick={handleModalDelete}>Delete</PrimaryButton>
                </ModalActions>
            </NavResponsiveModal>

            <TabCard
                css={css`
                    padding: 2rem;
                `}
            >
                <Snackbar open={!!notification.message} handleClose={() => setNotification({})} {...notification} />
                <Grid
                    direction="column"
                    css={css`
                        flex-wrap: nowrap;
                    `}
                >
                    <Grid
                        container
                        css={css`
                            flex-wrap: nowrap;
                            align-items: center;
                        `}
                    >
                        <PrimaryButton
                            disabled={loading}
                            onClick={() =>
                                upsertTags({
                                    variables: {
                                        tags: [
                                            {
                                                client_id: user_id,
                                                tag: ` New Tag ${ownedTags.length}`,
                                                route_tag: false,
                                                accessorial_tag: false,
                                            },
                                        ],
                                    },
                                })
                            }
                            css={css`
                                margin-right: 1rem;
                            `}
                        >
                            Add New Tag
                        </PrimaryButton>
                        <PrimaryButton disabled={loading} onClick={handleSave}>
                            Save Tags
                        </PrimaryButton>
                    </Grid>
                    {updated.map((tag, idx) => (
                        <Grid
                            container
                            css={css`
                                flex-wrap: nowrap;
                                align-items: center;
                                margin-top: 2rem;
                                margin-top: 2rem;
                            `}
                            key={tag.tag_id}
                        >
                            <Grid
                                item
                                css={css`
                                    width: 300px;
                                    margin-right: 1rem;
                                `}
                            >
                                <TextField
                                    variant="outlined"
                                    fullWidth
                                    label="Tag"
                                    value={tag.tag || ''}
                                    onChange={(e) =>
                                        setUpdates((prev) => ({
                                            ...prev,
                                            [tag.tag_id]: { ...tag, tag: e.target.value },
                                        }))
                                    }
                                />
                            </Grid>
                            <Grid
                                item
                                css={css`
                                    width: 300px;
                                    margin-right: 1rem;
                                `}
                            >
                                <Autocomplete
                                    multiple
                                    disableCloseOnSelect
                                    value={Object.keys(TAG_TYPES)
                                        .filter((type) => tag[type])
                                        .map((type) => ({ value: type, label: TAG_TYPES[type] }))}
                                    onChange={(e, options) => {
                                        const selected = Object.fromEntries(
                                            options.map((option) => [option.value, true])
                                        );
                                        setUpdates((prev) => ({
                                            ...prev,
                                            [tag.tag_id]: {
                                                ...tag,
                                                ...Object.fromEntries(
                                                    Object.keys(TAG_TYPES).map((type) => [type, selected[type]])
                                                ),
                                            },
                                        }));
                                    }}
                                    options={Object.entries(TAG_TYPES).map(([value, label]) => ({
                                        value,
                                        label,
                                    }))}
                                    getOptionSelected={(option, value) => {
                                        return option.value === value.value;
                                    }}
                                    getOptionLabel={(option) => option.label}
                                    renderTags={(options) => {
                                        return <span>{options.length} Type(s) selected</span>;
                                    }}
                                    renderOption={(option, { selected }) => {
                                        return (
                                            <li key={option.value}>
                                                <Checkbox color="primary" checked={selected} />
                                                {option.label}
                                            </li>
                                        );
                                    }}
                                    renderInput={(params) => <TextField variant="outlined" {...params} label="Types" />}
                                />
                            </Grid>
                            <Grid
                                item
                                css={css`
                                    margin-right: 1rem;
                                `}
                            >
                                <AlwaysApplyToggle
                                    checked={(tag.always_apply_clients || []).some((c) => c.client_id === user_id)}
                                    onToggle={(value) =>
                                        setUpdates((prev) => {
                                            const current = tag.always_apply_clients || [];
                                            const next = value
                                                ? current.some((c) => c.client_id === user_id)
                                                    ? current
                                                    : [...current, { client_id: user_id }]
                                                : current.filter((c) => c.client_id !== user_id);
                                            return {
                                                ...prev,
                                                [tag.tag_id]: { ...tag, always_apply_clients: next },
                                            };
                                        })
                                    }
                                />
                            </Grid>
                            <Grid item>
                                <IconButton disabled={loading} onClick={() => setDeleteModal(tag)}>
                                    <DeleteForeverOutlined fontSize="large" color={loading ? 'disabled' : 'error'} />
                                </IconButton>
                            </Grid>
                        </Grid>
                    ))}
                    {orgTags.length > 0 && (
                        <>
                            <Grid
                                item
                                css={css`
                                    display: flex;
                                    flex-direction: column;
                                    align-items: flex-start;
                                    margin-top: 3rem;
                                `}
                            >
                                <Body1
                                    css={css`
                                        font-weight: 700;
                                    `}
                                >
                                    Organization Tags
                                </Body1>
                                <Body1>Managed by your organization — read only.</Body1>
                            </Grid>
                            {[...orgTags]
                                .sort((a, b) => a.tag.localeCompare(b.tag))
                                .map((tag) => (
                                    <Grid
                                        container
                                        key={tag.tag_id}
                                        css={css`
                                            flex-wrap: nowrap;
                                            align-items: center;
                                            margin-top: 1rem;
                                        `}
                                    >
                                        <Grid
                                            item
                                            css={css`
                                                width: 300px;
                                                margin-right: 1rem;
                                            `}
                                        >
                                            <TextField
                                                variant="outlined"
                                                fullWidth
                                                label="Tag"
                                                value={tag.tag || ''}
                                                disabled
                                            />
                                        </Grid>
                                        <Grid
                                            item
                                            css={css`
                                                width: 300px;
                                                margin-right: 1rem;
                                            `}
                                        >
                                            <TextField
                                                variant="outlined"
                                                fullWidth
                                                label="Types"
                                                value={Object.keys(TAG_TYPES)
                                                    .filter((type) => tag[type])
                                                    .map((type) => TAG_TYPES[type])
                                                    .join(', ')}
                                                disabled
                                            />
                                        </Grid>
                                    </Grid>
                                ))}
                        </>
                    )}
                </Grid>
            </TabCard>
        </>
    );
};

export default CustomTags;
